import React from "react";

import Panel from "./Panel";
import { Side, Size } from "../../types/system/Layout";
import RenderOrientation from "../../types/enums/RenderOrientation";
import Toolbar from "../toolbar/Toolbar";

import "./RecordsPanel.scss";
import Textbox from "../inputform/Textbox";
import Button from "../buttons/Button";
import IconStyles from "../../types/enums/IconStyles";

interface RecordsPanelProps<T> {
    records?: T[];

    title?: string;
    typeName?: string;

    additionalMessage?: string;

    editField?: keyof T;
    minCount?: number;
    inlineRemove?: boolean;
    noRecordTitle?: boolean;

    clearanceSide?: Side;
    clearanceSize?: Size;

    bufferSide?: Side;
    bufferSize?: Size;

    editor?: (
        record: T,
        index: number,
        update: (updated: T) => void,
    ) => JSX.Element;
    customView?: (index: number, record: T) => JSX.Element;
    canRemove?: (record: T, index: number) => boolean;
    onAdd?: () => void;
    onDelete?: (record: T) => void;
    onChange?: (record: T, updated: Partial<T>) => void;
}

class RecordsPanel<T> extends React.Component<RecordsPanelProps<T>> {
    rowRemovable(record: T, index: number): boolean {
        return (
            (this.props.canRemove?.(record, index) ?? true) &&
            (!this.props.minCount || this.props.minCount >= index) &&
            !this.props.inlineRemove
        );
    }

    render(): React.ReactNode {
        return (
            <Panel
                className="records-panel"
                title={
                    this.props.title ?? `${this.props.typeName ?? "No Type"}`
                }
                bufferSide={this.props.bufferSide}
                bufferSize={this.props.bufferSize}
                clearanceSide={this.props.clearanceSide}
                clearanceSize={this.props.clearanceSize}
                onAdd={this.props.onAdd}
                addTooltip={`Add ${this.props.typeName ?? "No Type"}`}>
                {this.props.additionalMessage && (
                    <div
                        className="info"
                        style={{ position: "relative", top: "-10px" }}>
                        {this.props.additionalMessage}
                    </div>
                )}

                <div className="flex-column spaced">
                    {this.props.records?.map((item, index) => {
                        return (
                            <Panel
                                key={index}
                                className="record"
                                bufferSide={Side.All}
                                bufferSize={Size.Small}
                                title={
                                    this.props.noRecordTitle
                                        ? undefined
                                        : `${
                                              this.props.typeName ?? "No Type"
                                          } ${index + 1}`
                                }>
                                {this.rowRemovable(item, index) &&
                                    this.props.onDelete && (
                                        <Toolbar
                                            direction={
                                                RenderOrientation.RightLeft
                                            }>
                                            <Button
                                                icon="close"
                                                iconStyle={IconStyles.Red}
                                                onClick={() =>
                                                    this.props.onDelete(item)
                                                }
                                                tooltip={`Delete ${
                                                    this.props.typeName ??
                                                    "No Type"
                                                }`}
                                                tooltipPlace="bottom"
                                            />
                                        </Toolbar>
                                    )}

                                <div className="flex-row spaced">
                                    {this.props.customView ? (
                                        this.props.customView(index, item)
                                    ) : (
                                        <>
                                            <div className="records-input">
                                                {this.props.editor ? (
                                                    this.props.editor(
                                                        item,
                                                        index,
                                                        updated =>
                                                            this.props.onChange?.(
                                                                item,
                                                                updated,
                                                            ),
                                                    )
                                                ) : (
                                                    <Textbox
                                                        value={String(
                                                            item[
                                                                this.props
                                                                    .editField as keyof T
                                                            ] as any,
                                                        )}
                                                        onChange={value =>
                                                            this.props.onChange?.(
                                                                item,
                                                                {
                                                                    [this.props
                                                                        .editField as keyof T]:
                                                                        value,
                                                                } as Partial<T>,
                                                            )
                                                        }
                                                    />
                                                )}
                                            </div>

                                            {this.props.inlineRemove && (
                                                <div className="records-remove">
                                                    <Button
                                                        icon="close"
                                                        iconStyle={
                                                            IconStyles.Red
                                                        }
                                                        disabled={
                                                            !!this.props
                                                                .canRemove &&
                                                            !this.props.canRemove?.(
                                                                item,
                                                                index,
                                                            )
                                                        }
                                                        onClick={() =>
                                                            this.props.onDelete?.(
                                                                item,
                                                            )
                                                        }
                                                        tooltip={`Delete ${
                                                            this.props
                                                                .typeName ??
                                                            "No Type"
                                                        }`}
                                                        tooltipPlace="bottom"
                                                    />
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </Panel>
                        );
                    })}
                </div>
            </Panel>
        );
    }
}

export default RecordsPanel;
